import React, { useEffect, useMemo, useRef, useState } from 'react';

export default function SelectField({ value, onChange, options = [], placeholder = 'Select', disabled = false }) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const btnRef = useRef(null);
  const listRef = useRef(null);
  const wrapperRef = useRef(null);

  const items = useMemo(() => {
    const base = Array.isArray(options) ? options : [];
    if (placeholder) {
      const hasAll = base.some(o => o && o.value === '__all__');
      return hasAll ? base : [{ value: '__all__', label: placeholder }, ...base];
    }
    return base;
  }, [options, placeholder]);

  const selectedIndex = useMemo(() => {
    const idx = items.findIndex(o => o && o.value === value);
    return idx >= 0 ? idx : (items.length > 0 ? 0 : -1);
  }, [items, value]);

  const selectedLabel = useMemo(() => {
    const it = selectedIndex >= 0 ? items[selectedIndex] : null;
    return it ? String(it.label) : '';
  }, [items, selectedIndex]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      try {
        if (!wrapperRef.current) return;
        if (!wrapperRef.current.contains(e.target)) setOpen(false);
      } catch {}
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const openMenu = () => {
    if (disabled) return;
    setOpen(true);
    setHighlight(selectedIndex);
  };
  const closeMenu = () => setOpen(false);

  const commitSelection = (idx) => {
    const it = items[idx];
    if (!it) return;
    if (it.value !== value) onChange(it.value);
    closeMenu();
    try { btnRef.current && btnRef.current.focus(); } catch {}
  };

  const onButtonKeyDown = (e) => {
    if (disabled) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) { openMenu(); } else { setHighlight(h => Math.min(items.length - 1, (h < 0 ? 0 : h + 1))); }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!open) { openMenu(); } else { setHighlight(h => Math.max(0, (h < 0 ? 0 : h - 1))); }
        break;
      case 'Enter':
      case ' ': // Space
        e.preventDefault();
        if (!open) { openMenu(); } else if (highlight >= 0) { commitSelection(highlight); }
        break;
      case 'Escape':
        if (open) { e.preventDefault(); closeMenu(); }
        break;
      default:
        break;
    }
  };

  const onListKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); setHighlight(h => Math.min(items.length - 1, (h < 0 ? 0 : h + 1))); break;
      case 'ArrowUp': e.preventDefault(); setHighlight(h => Math.max(0, (h < 0 ? 0 : h - 1))); break;
      case 'Enter': e.preventDefault(); if (highlight >= 0) commitSelection(highlight); break;
      case 'Escape': e.preventDefault(); closeMenu(); break;
      case 'Tab': closeMenu(); break;
      default: break;
    }
  };

  useEffect(() => {
    if (!open) return;
    try {
      const el = listRef.current?.querySelector('[data-highlight="true"]');
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    } catch {}
  }, [open, highlight]);

  return (
    <div ref={wrapperRef} className="custom-select" data-disabled={disabled ? 'true' : 'false'}>
      <button
        ref={btnRef}
        type="button"
        className="custom-select__button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls="custom-select-listbox"
        aria-disabled={disabled}
        onClick={() => (open ? closeMenu() : openMenu())}
        onKeyDown={onButtonKeyDown}
        disabled={disabled}
      >
        <span className="custom-select__label">{selectedLabel || placeholder}</span>
        <span className="custom-select__chevron" aria-hidden>▾</span>
      </button>
      {open && (
        <div
          ref={listRef}
          id="custom-select-listbox"
          className="custom-select__menu"
          role="listbox"
          tabIndex={-1}
          onKeyDown={onListKeyDown}
        >
          {items.map((opt, idx) => {
            const selected = value === opt.value;
            const highlighted = idx === highlight;
            return (
              <div
                key={opt.value}
                role="option"
                aria-selected={selected}
                className={`custom-select__option${selected ? ' is-selected' : ''}${highlighted ? ' is-active' : ''}`}
                data-highlight={highlighted ? 'true' : 'false'}
                onMouseEnter={() => setHighlight(idx)}
                onMouseDown={(e) => { e.preventDefault(); commitSelection(idx); }}
              >
                {opt.label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
